import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Mic, PhoneCall, Clock, Activity, RefreshCw, Loader2,
  Phone, MessageSquare, TrendingUp, ChevronRight, AlertCircle,
  CheckCircle2, Star, BarChart3, Headphones
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function CallStatusBadge({ status }) {
  const map = {
    registered: { label: 'Registrada', cls: 'bg-blue-100 text-blue-700' },
    ongoing: { label: 'En curso', cls: 'bg-green-100 text-green-700' },
    ended: { label: 'Terminada', cls: 'bg-gray-100 text-gray-600' },
    error: { label: 'Error', cls: 'bg-red-100 text-red-700' },
  };
  const s = map[status] || { label: status || '—', cls: 'bg-gray-100 text-gray-600' };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>{s.label}</span>;
}

function SentimentBadge({ sentiment }) {
  if (!sentiment) return null;
  const map = { positive: { label: '😊 Positivo', cls: 'text-green-600' }, neutral: { label: '😐 Neutral', cls: 'text-gray-500' }, negative: { label: '😟 Negativo', cls: 'text-red-500' } };
  const s = map[sentiment] || { label: sentiment, cls: 'text-gray-500' };
  return <span className={`text-xs ${s.cls}`}>{s.label}</span>;
}

function SecondsToDuration(secs) {
  if (!secs && secs !== 0) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function VoiceAgent() {
  const [agentData, setAgentData] = useState(null);
  const [agentLoading, setAgentLoading] = useState(true);
  const [calls, setCalls] = useState([]);
  const [callsLoading, setCallsLoading] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [tab, setTab] = useState('overview');

  const token = localStorage.getItem('restaurant_token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  useEffect(() => { loadAgent(); }, []);

  async function loadAgent() {
    setAgentLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/retell/my-agent`, { headers });
      if (r.ok) setAgentData(await r.json());
    } catch {}
    finally { setAgentLoading(false); }
  }

  async function loadCalls() {
    setCallsLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/retell/my-calls?limit=30`, { headers });
      if (r.ok) { const d = await r.json(); setCalls(d.calls || []); }
    } catch {}
    finally { setCallsLoading(false); }
  }

  const agent = agentData?.agent;
  const assignment = agentData?.assignment;

  // Stats
  const totalCalls = calls.length;
  const endedCalls = calls.filter(c => c.call_status === 'ended');
  const avgDuration = endedCalls.length
    ? Math.round(endedCalls.reduce((s, c) => s + (c.duration_ms || 0), 0) / endedCalls.length / 1000)
    : 0;
  const sentimentCounts = calls.reduce((acc, c) => {
    const s = c.call_analysis?.user_sentiment;
    if (s) acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Mic className="h-6 w-6 text-orange-500" />Agente de Voz IA
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Atiende llamadas de reservaciones automáticamente con inteligencia artificial.
        </p>
      </div>

      {agentLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-10 w-10 animate-spin text-orange-500" />
        </div>
      ) : (
        <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v === 'calls' && calls.length === 0) loadCalls(); }}>
          <TabsList>
            <TabsTrigger value="overview"><Activity className="h-4 w-4 mr-1.5" />Resumen</TabsTrigger>
            <TabsTrigger value="calls"><PhoneCall className="h-4 w-4 mr-1.5" />Llamadas</TabsTrigger>
            {selectedCall && <TabsTrigger value="transcript"><MessageSquare className="h-4 w-4 mr-1.5" />Transcripción</TabsTrigger>}
          </TabsList>

          {/* ── OVERVIEW ── */}
          <TabsContent value="overview" className="space-y-4 mt-4">
            {/* Setup banner when not assigned */}
            {!agentData?.assigned && (
              <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl">
                <Mic className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">Agente de voz pendiente de activación</p>
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">Contacta a tu administrador de OkReserva para habilitar el agente de voz IA. Una vez asignado, todas las funciones estarán disponibles automáticamente.</p>
                </div>
              </div>
            )}
            {/* Agent Card */}
            <Card className="border-orange-100 dark:border-orange-900/30">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center">
                      <Mic className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <CardTitle>{agent?.agent_name || 'Agente de Voz IA'}</CardTitle>
                      <CardDescription>{agentData?.assigned ? `Activo · ${agent?.language || 'es-419'}` : 'Pendiente de configuración'}</CardDescription>
                    </div>
                  </div>
                  <Badge className={agentData?.assigned ? "bg-green-100 text-green-700 border-green-200" : "bg-gray-100 text-gray-500 border-gray-200"}>
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" />{agentData?.assigned ? 'Activo' : 'Sin asignar'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <InfoTile label="Voz" value={agent?.voice_id?.split('-').slice(1).join(' ') || '—'} />
                  <InfoTile label="Idioma" value={agent?.language || '—'} />
                  <InfoTile label="Duración máx." value={`${Math.round((agent?.max_call_duration_ms || 3600000) / 60000)} min`} />
                  <InfoTile label="Silencio máx." value={`${Math.round((agent?.end_call_after_silence_ms || 600000) / 1000)} s`} />
                </div>
                {agent?.begin_message && (
                  <div className="mt-4 bg-orange-50 dark:bg-orange-900/10 rounded-lg p-3">
                    <p className="text-xs font-medium text-orange-700 dark:text-orange-400 mb-1">Saludo inicial</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 italic">"{agent.begin_message}"</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Stats */}
            {calls.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard icon={PhoneCall} label="Total llamadas" value={totalCalls} color="orange" />
                <StatCard icon={Clock} label="Duración promedio" value={SecondsToDuration(avgDuration)} color="blue" />
                <StatCard icon={Star} label="Satisfacción positiva" value={`${sentimentCounts.positive || 0} / ${totalCalls}`} color="green" />
                <StatCard icon={Activity} label="Finalizadas" value={endedCalls.length} color="gray" />
              </div>
            )}

            {calls.length === 0 && (
              <Card className="border-dashed">
                <CardContent className="py-8 text-center">
                  <PhoneCall className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No hay llamadas registradas aún.</p>
                  <Button variant="outline" size="sm" className="mt-3" onClick={() => { setTab('calls'); loadCalls(); }}>
                    Ver historial de llamadas
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ── CALLS ── */}
          <TabsContent value="calls" className="space-y-4 mt-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">{calls.length} llamada(s)</p>
              <Button variant="outline" size="sm" onClick={loadCalls} disabled={callsLoading}>
                <RefreshCw className={`h-4 w-4 mr-1.5 ${callsLoading ? 'animate-spin' : ''}`} />Actualizar
              </Button>
            </div>
            {callsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
              </div>
            ) : calls.length === 0 ? (
              <Card><CardContent className="py-12 text-center">
                <PhoneCall className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No hay llamadas registradas aún.</p>
                <p className="text-sm text-gray-400">Las llamadas aparecerán aquí cuando tus clientes contacten al agente.</p>
              </CardContent></Card>
            ) : (
              <Card>
                <div className="divide-y dark:divide-gray-700">
                  {calls.map(call => (
                    <div key={call.call_id}
                      className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                      onClick={() => { setSelectedCall(call); setTab('transcript'); }}>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center">
                          <PhoneCall className="h-4 w-4 text-orange-500" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {call.from_number || call.caller_id || 'Número desconocido'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {call.start_timestamp ? new Date(call.start_timestamp).toLocaleString('es-MX') : '—'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {call.call_analysis?.user_sentiment && (
                          <SentimentBadge sentiment={call.call_analysis.user_sentiment} />
                        )}
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {SecondsToDuration(call.duration_ms ? Math.round(call.duration_ms / 1000) : null)}
                        </span>
                        <CallStatusBadge status={call.call_status} />
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </TabsContent>

          {/* ── TRANSCRIPT ── */}
          {selectedCall && (
            <TabsContent value="transcript" className="space-y-4 mt-4">
              <div className="flex items-center gap-3 mb-4">
                <Button variant="ghost" size="sm" onClick={() => setTab('calls')}>← Volver</Button>
                <div>
                  <p className="text-sm font-medium">{selectedCall.from_number || 'Número desconocido'}</p>
                  <p className="text-xs text-gray-500">
                    {selectedCall.start_timestamp ? new Date(selectedCall.start_timestamp).toLocaleString('es-MX') : '—'}
                    {' · '}
                    {SecondsToDuration(selectedCall.duration_ms ? Math.round(selectedCall.duration_ms / 1000) : null)}
                  </p>
                </div>
                <div className="ml-auto"><CallStatusBadge status={selectedCall.call_status} /></div>
              </div>

              {selectedCall.call_analysis && (
                <Card>
                  <CardHeader><CardTitle className="text-base">Análisis de la llamada</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      {selectedCall.call_analysis.call_summary && (
                        <div className="col-span-full">
                          <p className="text-xs font-medium text-gray-500 mb-1">Resumen</p>
                          <p className="text-gray-700 dark:text-gray-300">{selectedCall.call_analysis.call_summary}</p>
                        </div>
                      )}
                      {selectedCall.call_analysis.user_sentiment && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Sentimiento</p>
                          <SentimentBadge sentiment={selectedCall.call_analysis.user_sentiment} />
                        </div>
                      )}
                      {selectedCall.call_analysis.call_successful !== undefined && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Éxito</p>
                          {selectedCall.call_analysis.call_successful
                            ? <span className="text-green-600 text-sm">✓ Exitosa</span>
                            : <span className="text-red-500 text-sm">✗ No exitosa</span>}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Headphones className="h-4 w-4" />Transcripción</CardTitle></CardHeader>
                <CardContent>
                  {selectedCall.transcript_object?.length > 0 ? (
                    <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                      {selectedCall.transcript_object.map((msg, i) => (
                        <div key={i} className={`flex gap-3 ${msg.role === 'agent' ? '' : 'flex-row-reverse'}`}>
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold
                            ${msg.role === 'agent' ? 'bg-orange-500' : 'bg-gray-400'}`}>
                            {msg.role === 'agent' ? 'A' : 'C'}
                          </div>
                          <div className={`max-w-[75%] px-3 py-2 rounded-lg text-sm
                            ${msg.role === 'agent'
                              ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-900 dark:text-orange-100'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}`}>
                            {msg.content}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : selectedCall.transcript ? (
                    <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">{selectedCall.transcript}</p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">Sin transcripción disponible para esta llamada.</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      )}
    </div>
  );
}

function InfoTile({ label, value }) {
  return (
    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-0.5">{value}</p>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    orange: 'bg-orange-50 text-orange-500 dark:bg-orange-900/20',
    blue: 'bg-blue-50 text-blue-500 dark:bg-blue-900/20',
    green: 'bg-green-50 text-green-500 dark:bg-green-900/20',
    gray: 'bg-gray-100 text-gray-500 dark:bg-gray-700',
  };
  return (
    <Card>
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colors[color]}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
